import { KeyboardEventHandler, useState } from 'react';

import { Close } from '@mui/icons-material';
import { IconButton, Stack, SxProps, Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { UseFormRegisterReturn } from 'react-hook-form';

import ZoomGlass from '@/shared/ui/icons/ZoomGlass.svg';

import { BlurGuardInputBase } from './BlurGuardInputBase';

export interface ITextFieldSearchProps {
  sx?: SxProps<Theme>;
  value: string;
  setValue: (value: string) => void;
  onSubmit?: () => void;
  register?: UseFormRegisterReturn;
  form?: string;
  placeholder?: string;
}

/**
 * Инпут для ввода пароля
 * @param root0 - пропсы
 * @param root0.value - значение инпута
 * @param root0.setValue - переписывание значение инпута
 * @param root0.register - register из useForm
 * @param root0.sx - опционально, стили sx
 * @param root0.onSubmit - При нажатии enter или кнопки
 * @param root0.form - id формы для связывание с формой
 * @param root0.placeholder - текст плейсхолдера, по умолчанию «Найти»
 */
export const TextFieldSearch = ({
  sx,
  register,
  value,
  setValue,
  onSubmit,
  form,
  placeholder = 'Найти'
}: ITextFieldSearchProps) => {
  const [isFocused, setFocused] = useState(false);
  const { shape, palette } = useTheme();

  /**
   * Проверяет является ли инпут частью формы, функция для уменьшения сложности
   * @param arg - значение которое вернется если инпут не часть формы
   */
  const getValueIfInputNotForm = <T,>(arg: T) => {
    if (register) return undefined;

    return arg;
  };

  const isFilled = value.length > 0;
  const isActive = isFocused || isFilled;

  const renderCrossButton = (
    <IconButton
      sx={theme => ({
        borderRadius: `0 ${shape.borderL} ${shape.borderL} 0`,
        height: '100%',
        '&:not(:hover) svg path': {
          fill: theme.palette.grey[500]
        }
      })}
      onClick={() => setValue('')}
    >
      <Close />
    </IconButton>
  );

  return (
    <Stack direction='row' width='100%' height={40} gap={0.5} sx={sx}>
      <BlurGuardInputBase
        type='text'
        placeholder={placeholder}
        endAdornment={isFilled && renderCrossButton}
        onFocus={() => setFocused(true)}
        sx={{
          height: '100%',
          width: '100%',
          position: 'relative',
          maxWidth: 180,
          borderRadius: shape.borderL,
          transition: 'max-width 0.7s ease',
          '&:before': {
            content: '""',
            position: 'absolute',
            inset: '0',
            border: '1px solid transparent',
            background: palette.custom.border,
            borderRadius: 'inherit',
            mask: 'conic-gradient(#000 0 0) content-box exclude, conic-gradient(#000 0 0)'
          },
          '&.is-active': {
            maxWidth: 410,

            '&:before': {
              border: '2px solid transparent',
              background: palette.gradients.secondary
            }
          },
          '& .MuiInputBase-input': {
            zIndex: '1',
            borderRadius: isFilled
              ? `${shape.borderL} 0 0 ${shape.borderL}`
              : shape.borderL,
            height: '100%',
            padding: '0 12px'
          }
        }}
        inputProps={{ form }}
        className={isActive ? 'is-active' : ''}
        value={getValueIfInputNotForm(value)}
        onChange={event => setValue(event.currentTarget.value)}
        onKeyDown={getValueIfInputNotForm<KeyboardEventHandler>(e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit?.();
          }
        })}
        {...register}
        onBlur={() => setFocused(false)}
      />
      <IconButton
        sx={{
          width: 54,
          height: '100%',
          backgroundColor: palette.common.white,
          borderRadius: shape.borderL,
          border: `1px solid ${palette.custom.border}`,

          '&.is-active': {
            background: `radial-gradient(66% 294.64% at 87.2% -74.11%, ${palette.secondary.light} 0%, 
            ${palette.secondary.main} 32%, ${palette.secondary.dark} 87.5%)`,
            border: 'none',
            '& svg path': {
              stroke: palette.common.white
            }
          }
        }}
        className={isActive ? 'is-active' : ''}
        onClick={getValueIfInputNotForm(onSubmit)}
        form={form}
        type={register ? 'submit' : 'button'}
      >
        <ZoomGlass />
      </IconButton>
    </Stack>
  );
};
